import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useActiveTeam } from "@/hooks/use-team";
import { useTranslations } from "@/hooks/use-translations";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { MetaAdsParams } from "@/components/connectors/meta-ads-params";
import { api } from "@/lib/api";

/**
 * Meta Ads connector: the team's connection, which ad account the agent works
 * against, and the parameters it cannot fetch from Meta on its own.
 */
export function MetaConnector() {
	const t = useTranslations("connectors");
	const [connecting, setConnecting] = useState(false);
	const { activeTeam } = useActiveTeam();
	const teamId = activeTeam?.id ?? null;

	const queryClient = useQueryClient();
	const { data: metaStatus, isLoading: isLoadingStatus } = useQuery({
		queryKey: ["metaAdsStatus", teamId],
		queryFn: () => api.getMetaAdsStatus(teamId),
		retry: false,
	});

	const isConnected = metaStatus?.connected ?? false;
	// In a team only an owner or manager attaches the connection; everyone but a
	// viewer may pick which ad account it works against.
	const canConnect = metaStatus?.canConnect ?? false;
	const canUse = metaStatus?.canUse ?? false;
	const accounts = metaStatus?.adAccounts ?? [];
	const refreshStatus = () =>
		queryClient.invalidateQueries({ queryKey: ["metaAdsStatus", teamId] });

	const setDefaultAccount = useMutation({
		mutationFn: (dbId: string) => api.setDefaultMetaAdAccount(dbId, teamId),
		onSuccess: () => {
			toast.success(t("metaAdsAccountUpdated"));
			refreshStatus();
		},
		onError: (error) =>
			toast.error(
				error instanceof Error ? error.message : t("metaAdsAccountUpdateError"),
			),
	});

	const disconnect = useMutation({
		mutationFn: () => api.disconnectMetaAds(teamId),
		onSuccess: (result) => {
			toast.success(
				result.suspended
					? `${t("metaAdsDisconnected")} · ${t("metaAdsMonitorsSuspended")}: ${result.suspended}`
					: t("metaAdsDisconnected"),
			);
			refreshStatus();
		},
		onError: (error) =>
			toast.error(
				error instanceof Error ? error.message : t("metaAdsDisconnectError"),
			),
	});

	const handleDisconnect = () => {
		if (window.confirm(t("metaAdsDisconnectConfirm"))) disconnect.mutate();
	};

	const handleConnect = async () => {
		setConnecting(true);
		try {
			const { url } = await api.connectMetaAds(teamId);
			// Redirect the browser to the Meta OAuth dialog to grant access.
			window.location.href = url;
		} catch (error) {
			const message =
				error instanceof Error ? error.message : t("connectMetaAdsError");
			toast.error(message);
			setConnecting(false);
		}
	};

	return (
		<Card>
			{/* Name and logo are in the platform selector above the card. */}
			<div className="flex flex-wrap items-center justify-between gap-4 p-5">
				<div className="flex min-w-0 flex-wrap items-center gap-2">
					{isConnected && (
						<Badge tone="success">
							<CheckCircle2 size={13} />
							{t("metaAdsConnected")}
						</Badge>
					)}
					<p className="text-sm text-text-tertiary">
						{isConnected && metaStatus?.connection
							? `${t("metaAdsConnectedBy")}: ${metaStatus.connection.connectedByName ?? "—"}`
							: canConnect || isLoadingStatus
								? t("metaAdsDescription")
								: t("metaAdsConnectRestricted")}
					</p>
				</div>
				{canConnect && (
					<div className="flex items-center gap-2">
						{isConnected && (
							<Button
								variant="danger"
								size="sm"
								onClick={handleDisconnect}
								disabled={disconnect.isPending}
							>
								{t("metaAdsDisconnect")}
							</Button>
						)}
						<Button
							variant="primary"
							onClick={handleConnect}
							disabled={connecting || isLoadingStatus}
						>
							{connecting && <Loader2 size={16} className="animate-spin" />}
							{connecting
								? t("connecting")
								: isConnected
									? t("metaAdsReconnect")
									: t("connectMetaAds")}
						</Button>
					</div>
				)}
			</div>

			{isConnected && accounts.length > 0 && (
				<div className="border-t border-border-subtle p-5">
					<h3 className="text-sm font-medium text-text-primary">
						{t("metaAdsAccounts")}
					</h3>
					<ul className="mt-3 grid gap-2 sm:grid-cols-2">
						{accounts.map((account) => (
							<li key={account.dbId}>
								<button
									type="button"
									disabled={
										!canUse ||
										!account.hasToken ||
										account.isDefault ||
										setDefaultAccount.isPending
									}
									onClick={() => setDefaultAccount.mutate(account.dbId)}
									title={account.isDefault ? undefined : t("metaAdsUseAccount")}
									className={`w-full text-left rounded-xl border p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 ${
										account.isDefault
											? "border-brand-700 bg-brand-50"
											: "border-border-subtle bg-bg-surface hover:bg-brand-50 hover:border-brand-700 disabled:hover:bg-bg-surface disabled:hover:border-border-subtle disabled:cursor-not-allowed"
									}`}
								>
									<div className="flex items-center justify-between gap-2">
										<span className="text-sm font-medium text-text-primary">
											{account.name}
										</span>
										{account.isDefault ? (
											<Badge tone="brand">{t("metaAdsDefaultAccount")}</Badge>
										) : !account.hasToken ? (
											<Badge tone="warning">{t("metaAdsNoToken")}</Badge>
										) : null}
									</div>
									<span className="text-xs text-text-tertiary">
										act_{account.adAccountId}
									</span>
								</button>
							</li>
						))}
					</ul>
				</div>
			)}

			{isConnected && (
				<div className="border-t border-border-subtle p-5">
					<MetaAdsParams />
				</div>
			)}
		</Card>
	);
}
